import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { createWithId, getAll, getById, getBySingleQuery, remove, update, updateField } from "../utils/dbUtils";
import { db } from "../firebase";
import { asTimeZone } from "../utils/dateFormatter";
import moment from "moment-timezone";
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";

const normalizeOfferToken = (value) => {
    return isStringNotEmpty(value) ? String(value).trim().toLowerCase() : null;
};

const hasOtherRecord = (records = [], currentId = null) => {
    return records.some((record) => record.id !== currentId);
};

class OfferGroup {
    static collectionName = 'offerGroups';

    id = null;
    name = null;
    offerToken = null;
    legacyTokens = [];
    defaultProgramId = null;
    allowedProgramIds = [];
    enabled = false;

    constructor(offerGroup) {
        if (isObjectNotEmpty(offerGroup)) {
            if (isStringNotEmpty(offerGroup.id)) { this.id = offerGroup.id; }
            if (isStringNotEmpty(offerGroup.name)) { this.name = offerGroup.name; }
            if (isStringNotEmpty(offerGroup.offerToken)) { this.offerToken = normalizeOfferToken(offerGroup.offerToken); }
            if (isListNotEmpty(offerGroup.legacyTokens)) { this.legacyTokens = Array.from(new Set(offerGroup.legacyTokens.map(normalizeOfferToken).filter(Boolean))); }
            if (isStringNotEmpty(offerGroup.defaultProgramId)) { this.defaultProgramId = offerGroup.defaultProgramId; }
            if (isListNotEmpty(offerGroup.allowedProgramIds)) { this.allowedProgramIds = offerGroup.allowedProgramIds; }
            if (isBooleanTrue(offerGroup.enabled)) { this.enabled = true; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            offerToken: this.offerToken,
            legacyTokens: this.legacyTokens,
            defaultProgramId: this.defaultProgramId,
            allowedProgramIds: this.allowedProgramIds,
            enabled: this.enabled,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.id)) {
            throw new Error('Group code is required.');
        }
        if (isStringEmpty(this.name)) {
            throw new Error('Name is required.');
        }
        if (isStringEmpty(this.offerToken)) {
            throw new Error('Offer token is required.');
        }
        if (this.legacyTokens.includes(this.offerToken)) {
            throw new Error('Legacy tokens cannot include the canonical offer token.');
        }

        return true;
    }

    assertTokensAreUnique = async (currentId = null) => {
        const tokens = Array.from(new Set([this.offerToken, ...this.legacyTokens].filter(Boolean)));

        for (const token of tokens) {
            const offerTokenMatches = await getBySingleQuery(OfferGroup.collectionName, 'offerToken', token, '==');
            if (hasOtherRecord(offerTokenMatches, currentId)) {
                throw new Error(`Offer token "${token}" is already used by another offer group.`);
            }

            const legacyTokenMatches = await getBySingleQuery(OfferGroup.collectionName, 'legacyTokens', token, 'array-contains');
            if (hasOtherRecord(legacyTokenMatches, currentId)) {
                throw new Error(`Legacy token "${token}" is already used by another offer group.`);
            }
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            await this.assertTokensAreUnique();
            return await createWithId(OfferGroup.collectionName, this.id, this.toDto());
        }

        return null;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(OfferGroup.collectionName, id, fieldName, newValue);
        }

        throw new Error("id is required.");
    }

    static update = async (id, updatedOfferGroup, oldObj) => {
        const updatedObj = new OfferGroup(updatedOfferGroup);
        if (updatedObj.isValid() && id === updatedOfferGroup.id) {
            await updatedObj.assertTokensAreUnique(id);
            const success = await update(OfferGroup.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }
        } else {
            throw new Error('id and id field of the updated offer group must match.');
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(OfferGroup.collectionName, id);
        }

        throw new Error("id is required.");
    }

    static getAll = async () => {
        return await getAll(OfferGroup.collectionName);
    }

    static getById = async (id) => {
        return await getById(OfferGroup.collectionName, id);
    }

    static getByOfferToken = async (offerToken) => {
        const list = await getBySingleQuery(OfferGroup.collectionName, 'offerToken', offerToken, '==');
        return isListNotEmpty(list) ? list[0] : null;
    }

    static getCount = async (queryParameters = {}) => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        const q = query(collection(db, OfferGroup.collectionName), ...queryConstraints);
        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters = {}, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, OfferGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, OfferGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, OfferGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        const snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    }
}

export default OfferGroup;
